import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api } from '../services/api';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';

const grammarTopics = [
  { key: 'noun', label: 'Noun' },
  { key: 'verb', label: 'Verb' },
  { key: 'adjective', label: 'Adjective' },
  { key: 'adverb', label: 'Adverb' },
  { key: 'pronoun', label: 'Pronoun' },
  { key: 'preposition', label: 'Preposition' },
  { key: 'conjunction', label: 'Conjunction' },
  { key: 'interjection', label: 'Interjection' },
  { key: 'presentSimple', label: 'Present Simple' },
  { key: 'presentContinuous', label: 'Present Continuous' },
  { key: 'presentPerfect', label: 'Present Perfect' },
  { key: 'pastSimple', label: 'Past Simple' },
  { key: 'pastContinuous', label: 'Past Continuous' },
  { key: 'pastPerfect', label: 'Past Perfect' },
  { key: 'futureSimple', label: 'Future Simple' },
  { key: 'futureContinuous', label: 'Future Continuous' },
  { key: 'futurePerfect', label: 'Future Perfect' },
  { key: 'voiceNarration', label: 'Voice & Narration' },
  { key: 'modals', label: 'Modals' },
  { key: 'auxiliaries', label: 'Auxiliaries' },
];

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

export default function MemorizeGrammarPage({ selectedLanguage, userId }: { selectedLanguage?: string, userId: string }) {
  const { userData } = useUserData();
  const { currentUser } = useAuth();
  const actualUserId = userId || currentUser?.uid || '';
  const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
  const [selectedTopic, setSelectedTopic] = useState(grammarTopics[0].key);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [userCount, setUserCount] = useState<number>(0);
  const { addFavoriteGrammar, getFavoriteGrammar, addCompletedGrammar } = useUserData();
  const batchSize = 3;
  // Add state to track which grammar items are revealed
  const [revealed, setRevealed] = useState<{ [key: string]: boolean }>({});
  // Add state for user input and feedback
  const [userInputs, setUserInputs] = useState<{ [key: string]: string }>({});
  const [inputStatus, setInputStatus] = useState<{ [key: string]: 'idle' | 'correct' | 'incorrect' }>({});

  // Fetch memorized grammar data for the selected topic
  useEffect(() => {
    if (!actualUserId) return;
    setItems([]); // Clear old data immediately
    setLoading(true);
    api.getMemorizedGrammar(actualUserId, selectedTopic)
      .then(res => {
        setItems(res || []);
        setCurrentBatchIndex(0);
      })
      .catch(error => {
        console.error('Error fetching memorized grammar:', error);
      })
      .finally(() => setLoading(false));
    
    // Load favorite grammar IDs for this topic
    if (actualUserId) {
      api.getFavoriteGrammar(actualUserId, selectedTopic)
        .then(favs => {
          setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
        })
        .catch(err => {
          console.error('Error loading favorite grammar:', err);
        });
    }
    
    // Remove the unnecessary count API call
    // api.getMemorizeGrammarCount(actualUserId, selectedTopic)
    //   .then(count => {
    //     setUserCount(count);
    //   })
    //   .catch(err => {
    //     console.error('Error fetching memorize grammar count:', err);
    //     setUserCount(0);
    //   });
  }, [selectedTopic, actualUserId]);

  // Handle action (done, memorizeAgain, favorite)
  const handleAction = async (idx: number, status: 'memorized' | 'memorizeAgain' | 'favorite') => {
    if (!actualUserId || !items[idx]) return;
    const item = items[idx];
    if (status === 'favorite') {
      if (item && item.itemId && !favoriteIds.includes(item.itemId)) {
        // Optimistically update local state
        setFavoriteIds(prev => [...prev, item.itemId]);
        
        await addFavoriteGrammar(item.itemId, item.topic || selectedTopic);
        
        // Refresh favoriteIds after favoriting (in case of backend changes)
        if (actualUserId) {
          api.getFavoriteGrammar(actualUserId, selectedTopic)
            .then(favs => {
              setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
            })
            .catch(err => {
              console.error('Error refreshing favorite grammar:', err);
            });
        }
      }
      return;
    }
    if (status === 'memorized' || status === 'memorizeAgain') {
      if (item.itemId) {
        if (status === 'memorized') {
          await addCompletedGrammar(
            item.itemId,
            item.topic || selectedTopic
          );
        }
        await api.memorizeGrammar(
          actualUserId,
          item.itemId,
          item.topic || selectedTopic,
          status === 'memorized' ? 'done' : 'memorizeAgain'
        );
      }
    }
    const newItems = items.filter((_, i) => i !== idx);
    setItems(newItems);
    // If batch is now empty and not the first, go to previous batch
    const batchStart = currentBatchIndex * batchSize;
    if (batchStart >= newItems.length && currentBatchIndex > 0) {
      setCurrentBatchIndex(currentBatchIndex - 1);
    }
  };

  const batchStart = currentBatchIndex * batchSize;
  const batchEnd = batchStart + batchSize;
  const currentBatch = items.slice(batchStart, batchEnd);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
          <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Memorize Grammar</span>
        </h1>
      </div>
      <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          {/* Removed Selected Language text */}
          <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
            {grammarTopics.map(topic => (
              <button
                key={topic.key}
                className={`px-3 sm:px-4 md:px-6 py-2 rounded-full font-semibold text-xs sm:text-sm md:text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 ${selectedTopic === topic.key ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white scale-105' : 'bg-blue-100 text-blue-800 hover:bg-gradient-to-r hover:from-blue-200 hover:via-purple-100 hover:to-pink-100 hover:text-blue-900'}`}
                onClick={() => setSelectedTopic(topic.key)}
              >
                {topic.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-center text-lg text-blue-500 font-semibold py-12">Loading...</div>
        ) : (
          <>
            {/* Count and navigation */}
            <div className="text-center mb-4">
              <p className="text-gray-600">
                Showing {items.length === 0 ? 0 : batchStart + 1} - {Math.min(batchEnd, items.length)} of {items.length} sentences to memorize (Total: {items.length})
              </p>
            </div>
            <div className="flex flex-wrap gap-6 justify-center items-stretch">
              {currentBatch.map((item, idx) => (
                <div key={item.itemId} className="flex-1 max-w-xs min-w-[250px] bg-white/80 backdrop-blur-2xl border border-blue-200 rounded-3xl p-6 shadow-xl flex flex-col items-center gap-4 relative">
                  {/* Selected language on top */}
                  <div className="w-full text-center mb-2">
                    <div className="text-2xl font-bold mb-2 text-gray-900">{item[actualSelectedLanguage] || <span className='italic text-gray-400'>Translation not found</span>}</div>
                  </div>
                  {/* English example hidden by default, with view button */}
                  {revealed[item.itemId] ? (
                    <div className="text-base text-gray-700 mt-1">{item.en}</div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-10 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-300 rounded-xl flex items-center justify-center italic shadow-inner border border-gray-300">
                        <span className="mr-2 text-gray-400">
                          <svg xmlns='http://www.w3.org/2000/svg' className='inline w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 17a2 2 0 100-4 2 2 0 000 4zm6-2V9a6 6 0 10-12 0v6a2 2 0 002 2h8a2 2 0 002-2z' /></svg>
                        </span>
                        <span className="text-gray-400">Hidden</span>
                      </div>
                      <button
                        className="ml-2 px-4 py-1 rounded-xl bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs font-bold shadow hover:from-blue-500 hover:to-blue-700 transition flex items-center gap-1"
                        onClick={() => setRevealed(r => ({ ...r, [item.itemId]: true }))}
                      >
                        <svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>
                        View
                      </button>
                    </div>
                  )}
                  {/* Stylish input box for user to type English example */}
                  <form
                    className="mt-2 flex flex-col gap-2 w-full"
                    onSubmit={e => {
                      e.preventDefault();
                      const normalize = (str: string) =>
                        (str || '')
                          .toLowerCase()
                          .replace(/\s+/g, ' ')
                          .replace(/[^\w\s]/g, '')
                          .trim();
                      const userInput = normalize(userInputs[item.itemId]);
                      const correct = normalize(item.en);
                      setInputStatus(s => ({ ...s, [item.itemId]: userInput === correct ? 'correct' : 'incorrect' }));
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className={`w-full px-4 py-2 rounded-xl border text-base shadow focus:outline-none transition-all font-semibold tracking-wide bg-gradient-to-r from-gray-50 to-gray-100 placeholder-gray-400 ${inputStatus[item.itemId] === 'correct' ? 'bg-green-100 border-green-400' : inputStatus[item.itemId] === 'incorrect' ? 'bg-red-100 border-red-400' : 'border-gray-300'}`}
                        placeholder="Type the English example..."
                        value={userInputs[item.itemId] || ''}
                        onChange={e => {
                          setUserInputs(u => ({ ...u, [item.itemId]: e.target.value }));
                          setInputStatus(s => ({ ...s, [item.itemId]: 'idle' }));
                        }}
                      />
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white text-base font-bold shadow-lg hover:from-green-500 hover:to-green-700 transition flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-green-300"
                      >
                        <svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>
                        Check
                      </button>
                    </div>
                  </form>
                  <div className="flex-grow" />
                  {/* Action buttons aligned at the bottom */}
                  <div className="flex flex-wrap gap-1 sm:gap-2 justify-center w-full mt-2">
                    <button className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white text-xs sm:text-sm md:text-base font-bold shadow-lg hover:from-green-500 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300" onClick={() => handleAction(batchStart + idx, 'memorized')}>
                      <span className="text-xs">✅</span>
                      <span className="text-xs sm:text-sm">Done</span>
                    </button>
                    <button className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900 text-xs sm:text-sm md:text-base font-bold shadow-lg hover:from-yellow-400 hover:to-yellow-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300" onClick={() => handleAction(batchStart + idx, 'memorizeAgain')}>
                      <span className="text-xs">🔄</span>
                      <span className="text-xs sm:text-sm">Memorize Again</span>
                    </button>
                    <button
                      className={`flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm md:text-base font-bold shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${favoriteIds.includes(item.itemId) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-200 to-yellow-400 text-yellow-800 hover:from-yellow-300 hover:to-yellow-500'}`}
                      onClick={() => handleAction(batchStart + idx, 'favorite')}
                      disabled={favoriteIds.includes(item.itemId)}
                      title={favoriteIds.includes(item.itemId) ? 'Already favorited' : 'Favorite'}
                    >
                      <span className="text-xs">⭐</span>
                      <span className="text-xs sm:text-sm">{favoriteIds.includes(item.itemId) ? 'Favorited' : 'Favorite'}</span>
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="flex-1 text-center text-gray-500 font-semibold py-12">No items to memorize in this topic!</div>
              )}
              {/* Navigation buttons */}
              {items.length > batchSize && (
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-gray-200 to-gray-400 text-gray-800 font-semibold shadow-lg hover:from-gray-300 hover:to-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                    onClick={() => setCurrentBatchIndex(i => Math.max(0, i - 1))}
                    disabled={currentBatchIndex === 0}
                  >
                    Previous
                  </button>
                  <button
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white font-semibold shadow-lg hover:from-blue-500 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                    onClick={() => setCurrentBatchIndex(i => (batchEnd < items.length ? i + 1 : i))}
                    disabled={batchEnd >= items.length}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
} 